import React, { useReducer, useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, GridItem } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Portlet } from "@domestika-dev/prudencio/dist/atoms/Portlet";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";

import { useForms, useQuery } from "../../../js/modules";
import { siteKeys, RECAPTCHA_VERSION } from "../../../packs/grecaptcha";
import reducer from "../reducer";
import { processImages, filterValues, validateProject, loadDefaultAreas } from "../utils";
import {
  MAX_NUM_IMAGES,
  TITLE,
  AREAS,
  SIDEBAR,
  COVER,
  COURSE,
  SOFTWARES,
  MORE_CONTENT,
  CONTENT_ITEMS_MENU,
} from "../config";
import { getAreas, getSoftwares, getAssociableCourses } from "../actions";
import {
  AreasSelector,
  ContentItemsList,
  ContentItemMenu,
  ContentItemMenuFixed,
  CourseArea,
  Cover,
  ProjectFormMenu,
  ExtraDataArea,
  MadeAtSelectors,
  MultiUploader,
  ReCaptchaV2,
  ReCaptchaV3,
  SharedVisibility,
  SoftwaresSelector,
  Title,
} from "../component";

export const ProjectForm = ({
  isMobile,
  handleUpdateLocalStore,
  onChangeContentItemStore,
  onCreateContentItemStore,
  onRemoveContentItemStore,
  onDeleteProjectModal,
  onEmbedStartLoading,
  onEmbedStopLoading,
  onImageStartLoading,
  onImageStopLoading,
  onCoverStartLoading,
  onCoverStopLoading,
  onSaveProject,
  onSortContentItems,
  onFocusElement,
  focusedElement,
  showOnScroll,
  showOnScrollTop,
}) => {
  let query = useQuery();

  const intl = useIntl();

  const project = useSelector(state => state.projects);
  const contentItemsToShow = project.contentItems.filter(item => item._destroy === false);

  const [isValid, setIsValid] = useState();
  const [uploadedImages, setUploadedImages] = useState(0);

  const formDataRef = useRef(null);
  const mainMenuRef = useRef(null);
  const fixedMenuRef = useRef(null);

  const dispatch = useDispatch();

  const onGetAreas = useCallback(() => dispatch(getAreas()), [dispatch]);
  const onGetAssociableCourses = useCallback(id => dispatch(getAssociableCourses(id)), [dispatch]);
  const onGetSoftwares = useCallback(() => dispatch(getSoftwares()), [dispatch]);
  const measuredRef = useCallback(
    node => {
      node?.scrollIntoView({ behavior: "smooth" });
    },
    [focusedElement.id, project.contentItems.length]
  );

  const validateForm = () => {
    setIsValid(validateProject(project));
  };

  const { handleSubmit } = useForms(validateForm);

  useEffect(() => {
    onGetAreas();
    onGetSoftwares();

    if (query.get("after_copy")) {
      onFocusElement({ id: CONTENT_ITEMS_MENU });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    validateForm();
  }, [project]);

  useEffect(() => {
    if (project.id) {
      onGetAssociableCourses(project.id);
    }
  }, [project.id]);

  const onSubmit = (e, publish) => {
    e.preventDefault();

    const version =
      project.reCaptchaVersion === RECAPTCHA_VERSION.V3
        ? RECAPTCHA_VERSION.V3
        : RECAPTCHA_VERSION.V2;

    handleSubmit(formDataRef);

    onSaveProject(project, publish, version);
  };

  const onDelete = e => {
    e.preventDefault();

    onDeleteProjectModal(true);
  };

  const onEmbedSave = async e => {
    onEmbedStartLoading();
    onFocusElement({ id: MORE_CONTENT });

    if (!e.currentTarget.value) {
      onFocusElement({ id: null });
      onEmbedStopLoading();
      return;
    }

    await onSaveProject(project, false, RECAPTCHA_VERSION.V3);

    onFocusElement({ id: null });
    onEmbedStopLoading();
  };

  const onImagesLoad = async images => {
    onImageStartLoading();
    onFocusElement({ id: MORE_CONTENT });

    const correctImages = filterValues(images, "error");

    if (correctImages?.length <= 0) {
      onFocusElement({ id: null });
      onImageStopLoading();
      return;
    }

    const imagesToItems = processImages(correctImages, project.contentItems.length);
    const newProject = {
      ...project,
      contentItems: [...project.contentItems, ...imagesToItems],
    };

    await onSaveProject(newProject, false, RECAPTCHA_VERSION.V3);

    setUploadedImages(imagesToItems.length);

    onFocusElement({ id: null });
    onImageStopLoading();
  };

  const onAddContentItem = itemType => {
    setUploadedImages(0);

    onFocusElement({ id: null });
    onCreateContentItemStore(itemType);
  };

  const onSelectedElements = (elements, nameAttribute) => {
    const newSelection = [];

    elements?.forEach(item => newSelection.push(item.value));

    handleUpdateLocalStore({
      [nameAttribute]: newSelection,
    });

    if (!elements) {
      project[nameAttribute] = [];
    }
  };

  const onSelectedDate = (date, nameAttribute) => {
    handleUpdateLocalStore({
      [nameAttribute]: date.value,
    });

    if (!date) {
      project[nameAttribute] = [];
    }
  };

  if (project.loading) {
    return null;
  }

  const renderFloatyMenu = () => {
    return (
      <ContentItemMenuFixed
        reference={fixedMenuRef}
        top={showOnScrollTop}
        addContentItem={onAddContentItem}
        isMobile={isMobile}
        deleteProject={onDelete}
        saveDraft={e => onSubmit(e, false)}
        publishProject={e => onSubmit(e, true)}
        deleteDisabled={!project.id}
        publishDisabled={!isValid}
        canOrganize={contentItemsToShow.length > 1}
        organizeAction={onSortContentItems}
      />
    );
  };

  const renderMainMenu = () => {
    return (
      <div {...{ "data-label": "project-header-menu" }}>
        <ProjectFormMenu
          deleteProject={onDelete}
          saveDraft={e => onSubmit(e, false)}
          publishProject={e => onSubmit(e, true)}
          deleteDisabled={!project.id}
          publishDisabled={!isValid}
          isMobile={isMobile}
          reference={mainMenuRef}
        />
      </div>
    );
  };

  const renderTitle = () => {
    return (
      <div {...{ "data-label": "project-title" }}>
        <Title
          title={project.name}
          label={intl.formatMessage({ id: "projects.new.title_input.title" })}
          placeholderMessage={intl.formatMessage({
            id: "projects.new.title_input.placeholder",
          })}
          onChange={handleUpdateLocalStore}
          validateForm={validateForm}
          onFocusElement={() => onFocusElement({ id: TITLE })}
          maxLength={255}
          errors={project.errorMessage.name}
          marginTop={0}
        />
      </div>
    );
  };

  const renderAreas = () => {
    if (!project.areaIdsSaved && project.loadingCourse) {
      return null;
    }

    const defaultValue = loadDefaultAreas(project);

    return (
      <div {...{ "data-label": "project-areas" }}>
        <AreasSelector
          label={intl.formatMessage({ id: "projects.new.area.input.title" })}
          onChange={onSelectedElements}
          onFocusElement={() => onFocusElement({ id: AREAS })}
          defaultValue={defaultValue}
          options={project.areasList}
          loading={project.loadingAreas}
          placeholderMessage={intl.formatMessage({
            id: "projects.new.area.input.placeholder",
          })}
          noOptionsMessage={intl.formatMessage({
            id: "projects.new.area.input.no_results",
          })}
          errors={project.errorMessage.areas}
          marginTop={5}
        />
      </div>
    );
  };

  const renderMultiUploader = () => {
    return (
      <div data-label="project-multi-uploader">
        <MultiUploader
          maxNumFiles={MAX_NUM_IMAGES}
          validateForm={validateForm}
          onImagesLoad={onImagesLoad}
          loading={project.isImageLoading}
          maxUploadSizeMessage={intl.formatMessage(
            { id: "projects.new.image_upload.input.hint" },
            { size: 10 }
          )}
          descriptionMessage={intl.formatMessage(
            { id: "projects.new.image_upload.input.body.plural" },
            { limit: 15 }
          )}
          progressMessage={intl.formatMessage({
            id: "projects.new.image_upload.input.loading",
          })}
        />
      </div>
    );
  };

  const renderContentItemsMenu = () => {
    return (
      <ContentItemMenu
        addContentItem={onAddContentItem}
        canOrganize={contentItemsToShow.length > 1}
        organizeAction={onSortContentItems}
        marginTop={4}
        reference={focusedElement.id === CONTENT_ITEMS_MENU ? measuredRef : null}
      />
    );
  };

  const renderContentItemsList = () => {
    return (
      <div data-label="project-content-items">
        <ContentItemsList
          reference={measuredRef}
          contentItems={contentItemsToShow}
          onChange={(idElement, type, data) => onChangeContentItemStore(idElement, type, data)}
          onRemoveElement={onRemoveContentItemStore}
          onImagesLoad={onImagesLoad}
          validate={validateForm}
          isImageLoading={project.isImageLoading}
          isEmbedContentLoading={project.isEmbedContentLoading}
          onSetEmbedContent={onEmbedSave}
          focusedElement={focusedElement}
          onFocusElement={onFocusElement}
          uploadedImages={uploadedImages}
          isMobile={isMobile}
          errors={project.errorMessage.contentItems}
        />
      </div>
    );
  };

  const renderCover = () => {
    return (
      <div
        {...{ "data-label": "project-cover" }}
        ref={focusedElement.id === COVER ? measuredRef : null}
      >
        <Cover
          project={project}
          label={intl.formatMessage({ id: "projects.new.cover_image.input.title" })}
          maxUploadSizeMessage={intl.formatMessage({
            id: "projects.new.cover_image.input.hint",
          })}
          descriptionMessage={intl.formatMessage({
            id: "projects.new.cover_image.input.body",
          })}
          progressMessage={intl.formatMessage({
            id: "projects.new.cover_image.input.loading",
          })}
          maxNumFiles={1}
          minWidth={1200}
          minHeight={1200}
          validateForm={validateForm}
          isMobile={isMobile}
          onRemove={handleUpdateLocalStore}
          onCoverStartLoading={onCoverStartLoading}
          onCoverStopLoading={onCoverStopLoading}
          onSaveProject={onSaveProject}
          onFocusElement={() => onFocusElement({ id: COVER })}
          afterRemoveElement={() => onFocusElement({ id: SIDEBAR })}
          reCaptchaVersion={RECAPTCHA_VERSION.V3}
          marginTop={0}
        />
      </div>
    );
  };

  const renderCourse = () => {
    return (
      <CourseArea
        course={project.course}
        title={intl.formatMessage(
          { id: "projects.new.course_project_of" },
          { course: project.course?.title }
        )}
        label={intl.formatMessage({ id: "projects.new.course_project" })}
        placeholderMessage={intl.formatMessage({ id: "projects.new.select_course" })}
        associableCourses={project.associableCourses}
        onChange={handleUpdateLocalStore}
        onFocusElement={() => onFocusElement({ id: COURSE })}
        isLoadingCourse={project.loadingCourse}
        isLoadingAssociableCourses={project.loadingAssociableCourses}
        marginTop={5}
      />
    );
  };

  const renderSharedVisibility = () => {
    return (
      <div {...{ "data-label": "project-domestika-share" }}>
        <SharedVisibility
          label={intl.formatMessage({ id: "projects.new.share_on_domestika" })}
          checked={project.sharedVisibility}
          onChange={handleUpdateLocalStore}
          marginTop={5}
        />
      </div>
    );
  };

  const renderSoftwares = () => {
    return (
      <div {...{ "data-label": "project-softwares" }}>
        <SoftwaresSelector
          label={intl.formatMessage({ id: "projects.new.software.input.title" })}
          onChange={onSelectedElements}
          onFocusElement={() => onFocusElement({ id: SOFTWARES })}
          defaultValue={project.softwareIdsSaved}
          options={project.softwaresList}
          loading={project.loadingSoftwares}
          placeholderMessage={intl.formatMessage({
            id: "projects.new.software.input.placeholder",
          })}
          noOptionsMessage={intl.formatMessage({
            id: "projects.new.software.input.no_results",
          })}
          errors={project.errorMessage.softwares}
          marginTop={project.courseId ? 6 : 5}
        />
      </div>
    );
  };

  const renderPublishedDate = () => {
    return (
      <div {...{ "data-label": "project-publish-date" }}>
        <MadeAtSelectors
          loading={project.loading}
          days={project.days}
          months={project.months}
          years={project.years}
          dayPublished={project.dayPublished}
          monthPublished={project.monthPublished}
          yearPublished={project.yearPublished}
          onSelectedDate={onSelectedDate}
          label={intl.formatMessage({ id: "projects.new.date.input.title" })}
          dayPlaceholderMessage={intl.formatMessage({
            id: "projects.new.date.input.day.placeholder",
          })}
          monthPlaceholderMessage={intl.formatMessage({
            id: "projects.new.date.input.month.placeholder",
          })}
          yearPlaceholderMessage={intl.formatMessage({
            id: "projects.new.date.input.year.placeholder",
          })}
          marginTop={5}
        />
      </div>
    );
  };

  const renderExtraDataArea = () => {
    return (
      <ExtraDataArea
        project={project}
        handleChange={handleUpdateLocalStore}
        marginTop={5}
        onFocusElement={onFocusElement}
        focusedElement={focusedElement}
        reference={measuredRef}
      />
    );
  };

  const renderReCaptcha = () => {
    return (
      <>
        {project.reCaptchaVersion === RECAPTCHA_VERSION.V2 && (
          <ReCaptchaV2 sitekey={siteKeys.V2} currentLang={intl.locale} isMobile={isMobile} />
        )}

        {project.reCaptchaVersion === RECAPTCHA_VERSION.V3 && <ReCaptchaV3 sitekey={siteKeys.V3} />}
      </>
    );
  };

  return (
    <form ref={formDataRef} data-label="project-form">
      {showOnScroll && renderFloatyMenu()}

      <Grid
        gridTemplateColumns={["1fr", "1fr", "1fr 1fr 1fr"]}
        gridTemplateRows="auto"
        gridTemplateAreas={[
          `"main" "content" "sidebar" "menu"`,
          `"main" "content" "sidebar" "menu"`,
          `"menu menu menu" "main main sidebar"`,
        ]}
        gridGap={[4, 4, 5]}
      >
        {isMobile ? (
          <>
            <GridItem gridArea="menu">{renderMainMenu()}</GridItem>

            <GridItem gridArea="main" ref={focusedElement.id === TITLE ? measuredRef : null}>
              <Portlet paddingX={[4, 4, 5]} paddingY={[4, 4, 5]}>
                {renderTitle()}
                {renderAreas()}
              </Portlet>
            </GridItem>

            <GridItem
              gridArea="content"
              ref={focusedElement.id === MORE_CONTENT ? measuredRef : null}
            >
              <Portlet paddingX={[4, 4, 5]} paddingY={[4, 4, 5]}>
                {renderMultiUploader()}
                {renderContentItemsMenu()}
                {contentItemsToShow && renderContentItemsList()}
              </Portlet>
            </GridItem>
          </>
        ) : (
          <>
            <GridItem gridArea="menu">
              <Portlet paddingX={[4, 4, 5]} paddingY={[4, 4, 5]}>
                {renderMainMenu()}
              </Portlet>
            </GridItem>

            <GridItem gridArea="main" ref={focusedElement.id === TITLE ? measuredRef : null}>
              <Grid gridGap={[4, 4, 5]}>
                <GridItem>
                  <Portlet paddingX={[4, 4, 5]} paddingY={[4, 4, 5]}>
                    {renderTitle()}
                    {renderAreas()}
                  </Portlet>
                </GridItem>

                <GridItem>
                  <Portlet paddingX={[4, 4, 5]} paddingY={[4, 4, 5]}>
                    {renderMultiUploader()}
                    {renderContentItemsMenu()}
                    {contentItemsToShow && renderContentItemsList()}
                  </Portlet>
                </GridItem>
              </Grid>
            </GridItem>
          </>
        )}

        <GridItem gridArea="sidebar" ref={focusedElement.id === SIDEBAR ? measuredRef : null}>
          <Portlet paddingX={[4, 4, 5]} paddingY={[4, 4, 5]}>
            {renderCover()}
            {renderCourse()}
            {project.courseId && renderSharedVisibility()}
            {renderSoftwares()}
            {renderPublishedDate()}
            {renderExtraDataArea()}
            {renderReCaptcha()}
          </Portlet>
        </GridItem>
      </Grid>
    </form>
  );
};

ProjectForm.propTypes = {
  isMobile: PropTypes.bool,
  handleUpdateLocalStore: PropTypes.func,
  onSaveProject: PropTypes.func,
  onDeleteProjectModal: PropTypes.func,
  onChangeContentItemStore: PropTypes.func,
  onCreateContentItemStore: PropTypes.func,
  onRemoveContentItemStore: PropTypes.func,
  onImageStartLoading: PropTypes.func,
  onImageStopLoading: PropTypes.func,
  onCoverStartLoading: PropTypes.func,
  onCoverStopLoading: PropTypes.func,
  onEmbedStartLoading: PropTypes.func,
  onEmbedStopLoading: PropTypes.func,
  onSortContentItems: PropTypes.func,
  onFocusElement: PropTypes.func,
  focusedElement: PropTypes.object,
  showOnScroll: PropTypes.bool,
  showOnScrollTop: PropTypes.number,
};

ProjectForm.defaultProps = {
  isMobile: false,
  handleUpdateLocalStore: () => {},
  onSaveProject: () => {},
  onDeleteProjectModal: () => {},
  onChangeContentItemStore: () => {},
  onCreateContentItemStore: () => {},
  onRemoveContentItemStore: () => {},
  onImageStartLoading: () => {},
  onImageStopLoading: () => {},
  onCoverStartLoading: () => {},
  onCoverStopLoading: () => {},
  onEmbedStartLoading: () => {},
  onEmbedStopLoading: () => {},
  onSortContentItems: () => {},
  onFocusElement: () => {},
  focusedElement: {},
  showOnScroll: false,
  showOnScrollTop: 0,
};

export default ProjectForm;
